import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useState,
} from "react";
import {
  BentoSearchFilter,
  Organization,
  OrganizationAutopilotProfile,
  OrganizationBulkSendSettings,
  OrganizationProProfile,
  OrganizationProfile,
  OrganizationType,
  OrganizationUser,
  OrganizationVaProfile,
  UserIntegration,
  UserNotifications,
} from "schemas/dashboard";
import { EmailSettings } from "schemas/email";

import {
  GamificationProfile,
  OrganizationGamificationCredit,
} from "features/Influencer/HomePage/Gamification/schema";

/*
 * This is the Context for all Organization related values
 */

interface OrganizationUserContextInterface {
  currentUser: OrganizationUser | null;
  setCurrentUser: React.Dispatch<SetStateAction<OrganizationUser | null>>;
  currentOrg: Organization | null;
  setCurrentOrg: React.Dispatch<SetStateAction<Organization | null>>;
  clearOrganizationContext: () => void;
  isInfluencer: boolean;
  userIntegrations: UserIntegration[];
  setUserIntegrations: React.Dispatch<SetStateAction<UserIntegration[]>>;
  profile: OrganizationProfile | null;
  setProfile: React.Dispatch<SetStateAction<OrganizationProfile>>;
  userNotifications: UserNotifications | null;
  setUserNotifications: React.Dispatch<
    SetStateAction<UserNotifications | null>
  >;
  emailSettings: EmailSettings | null;
  setEmailSettings: React.Dispatch<SetStateAction<EmailSettings | null>>;
  hideCityDialog: boolean;
  setHideCityDialog: React.Dispatch<SetStateAction<boolean>>;
  includedBentoSearchFilterIds: number[];
  setIncludedBentoSearchFilterIds: React.Dispatch<SetStateAction<number[]>>;
  excludedBentoSearchFilterIds: number[];
  setExcludedBentoSearchFilterIds: React.Dispatch<SetStateAction<number[]>>;
  searchFilters: BentoSearchFilter[];
  setSearchFilters: Dispatch<SetStateAction<BentoSearchFilter[]>>;
  organizationVaProfile: OrganizationVaProfile | null;
  setOrganizationVaProfile: Dispatch<
    SetStateAction<OrganizationVaProfile | null>
  >;
  organizationBulkSendSettings: OrganizationBulkSendSettings | null;
  setOrganizationBulkSendSettings: Dispatch<
    SetStateAction<OrganizationBulkSendSettings | null>
  >;
  totalEmailsSent: number;
  setTotalEmailsSent: React.Dispatch<SetStateAction<number>>;
  organizationProProfile: OrganizationProProfile | null;
  setOrganizationProProfile: Dispatch<
    SetStateAction<OrganizationProProfile | null>
  >;
  organizationAutopilotProfile: OrganizationAutopilotProfile | null;
  setOrganizationAutopilotProfile: Dispatch<
    SetStateAction<OrganizationAutopilotProfile | null>
  >;
  gamificationProfile: GamificationProfile | null;
  setGamificationProfile: Dispatch<SetStateAction<GamificationProfile | null>>;
  showGamificationProfile: boolean;
  setShowGamificationProfile: Dispatch<SetStateAction<boolean>>;
  credits: OrganizationGamificationCredit[] | null;
  setCredits: Dispatch<SetStateAction<OrganizationGamificationCredit[] | null>>;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  currentUser: null,
  setCurrentUser: defaultContextMissingFunction,
  currentOrg: null,
  setCurrentOrg: defaultContextMissingFunction,
  clearOrganizationContext: defaultContextMissingFunction,
  isInfluencer: false,
  userIntegrations: [],
  setUserIntegrations: defaultContextMissingFunction,
  profile: null,
  setProfile: defaultContextMissingFunction,
  userNotifications: null,
  setUserNotifications: defaultContextMissingFunction,
  emailSettings: null,
  setEmailSettings: defaultContextMissingFunction,
  hideCityDialog: false,
  setHideCityDialog: defaultContextMissingFunction,
  includedBentoSearchFilterIds: [],
  setIncludedBentoSearchFilterIds: defaultContextMissingFunction,
  excludedBentoSearchFilterIds: [],
  setExcludedBentoSearchFilterIds: defaultContextMissingFunction,
  searchFilters: [],
  setSearchFilters: defaultContextMissingFunction,
  organizationVaProfile: null,
  setOrganizationVaProfile: defaultContextMissingFunction,
  organizationBulkSendSettings: null,
  setOrganizationBulkSendSettings: defaultContextMissingFunction,
  totalEmailsSent: 0,
  setTotalEmailsSent: defaultContextMissingFunction,
  organizationProProfile: null,
  setOrganizationProProfile: defaultContextMissingFunction,
  organizationAutopilotProfile: null,
  setOrganizationAutopilotProfile: defaultContextMissingFunction,
  gamificationProfile: null,
  setGamificationProfile: defaultContextMissingFunction,
  showGamificationProfile: false,
  setShowGamificationProfile: defaultContextMissingFunction,
  credits: null,
  setCredits: defaultContextMissingFunction,
};

const OrganizationUserContext =
  createContext<OrganizationUserContextInterface>(defaultInterface);

interface OrganizationUserProviderProps {
  children: React.ReactNode;
}

const OrganizationUserProvider = ({
  children,
}: OrganizationUserProviderProps) => {
  const [currentUser, setCurrentUser] = useState<OrganizationUser | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [profile, setProfile] = useState<OrganizationProfile>({
    id: 0,
  });
  const [userNotifications, setUserNotifications] =
    useState<UserNotifications | null>(null);

  const isInfluencer = currentOrg?.type === OrganizationType.INFLUENCER;

  const [userIntegrations, setUserIntegrations] = useState<UserIntegration[]>(
    [],
  );
  const [emailSettings, setEmailSettings] = useState<EmailSettings | null>(
    null,
  );
  const [hideCityDialog, setHideCityDialog] = useState<boolean>(false);
  const [includedBentoSearchFilterIds, setIncludedBentoSearchFilterIds] =
    useState<number[]>([]);
  const [excludedBentoSearchFilterIds, setExcludedBentoSearchFilterIds] =
    useState<number[]>([]);
  const [searchFilters, setSearchFilters] = useState<BentoSearchFilter[]>([]);
  const [organizationVaProfile, setOrganizationVaProfile] =
    useState<OrganizationVaProfile | null>(null);
  const [organizationProProfile, setOrganizationProProfile] =
    useState<OrganizationProProfile | null>(null);
  const [organizationAutopilotProfile, setOrganizationAutopilotProfile] =
    useState<OrganizationAutopilotProfile | null>(null);
  const [gamificationProfile, setGamificationProfile] =
    useState<GamificationProfile | null>(null);
  const [showGamificationProfile, setShowGamificationProfile] =
    useState<boolean>(false);

  const [organizationBulkSendSettings, setOrganizationBulkSendSettings] =
    useState<OrganizationBulkSendSettings | null>(null);
  const [totalEmailsSent, setTotalEmailsSent] = useState<number>(0);
  const [credits, setCredits] = useState<
    OrganizationGamificationCredit[] | null
  >(null);

  const clearOrganizationContext = () => {
    setCurrentUser(null);
    setCurrentOrg(null);
    setHideCityDialog(false);
  };

  return (
    <OrganizationUserContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        currentOrg,
        setCurrentOrg,
        clearOrganizationContext,
        isInfluencer,
        userIntegrations,
        setUserIntegrations,
        profile,
        setProfile,
        userNotifications,
        setUserNotifications,
        emailSettings,
        setEmailSettings,
        hideCityDialog,
        setHideCityDialog,
        includedBentoSearchFilterIds,
        setIncludedBentoSearchFilterIds,
        excludedBentoSearchFilterIds,
        setExcludedBentoSearchFilterIds,
        searchFilters,
        setSearchFilters,
        organizationVaProfile,
        setOrganizationVaProfile,
        organizationBulkSendSettings,
        setOrganizationBulkSendSettings,
        totalEmailsSent,
        setTotalEmailsSent,
        organizationProProfile,
        setOrganizationProProfile,
        organizationAutopilotProfile,
        setOrganizationAutopilotProfile,
        gamificationProfile,
        setGamificationProfile,
        showGamificationProfile,
        setShowGamificationProfile,
        credits,
        setCredits,
      }}
    >
      {children}
    </OrganizationUserContext.Provider>
  );
};

export { OrganizationUserProvider, OrganizationUserContext };
